import { EventReview } from "./EventReview";
import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus } from "lucide-react";
import type {
  EmailEventInput,
  EmailEventPreview,
  EmailEventResult,
  EmailEventSource,
  EmailEventSuggestion,
} from "@shared/emailCalendar";
import { calendarApi } from "@web/lib/calendarApi";
import { useCommand } from "@web/shell/commands";
import type { CommandId } from "@web/shell/keymap";
import { isApiError } from "@web/lib/api";
import { inboxApi } from "@web/lib/inboxApi";
import { timeZoneOptions, zoneLabel } from "@web/lib/timeZones";
import { registerUndo } from "@web/lib/undoRegistry";
import { CONFIRM_GUARD_MS } from "@web/feed/actions";
import { Button } from "@web/ui/Button";
import { Modal } from "@web/ui/Modal";
import { currentSuggestions, emailCalendarApi, nextDate } from "./api";
import { EventResult } from "./EventResult";
const field = "w-full min-w-0 rounded-lg border border-line bg-bg px-3 py-2 text-sm text-ink";
function errorWords(error: unknown) {
  return isApiError(error)
    ? (error.serverMessage ?? "Couldn't finish this step. Check the details and try again.")
    : "Couldn't reach the server. Your details are still here.";
}
function Zone({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (zone: string) => void;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1 text-sm">
      {label}
      <select className={field} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose time zone</option>
        {timeZoneOptions(value).map((zone) => (
          <option key={zone} value={zone}>
            {zoneLabel(zone)}
          </option>
        ))}
      </select>
    </label>
  );
}
function EventForm({ source, onClose }: { source: EmailEventSource; onClose: () => void }) {
  const client = useQueryClient();
  const accounts = useQuery({
    queryKey: ["calendarAccounts"],
    queryFn: () => calendarApi.accounts(),
  });
  const first = source.messages[0];
  const [messageKey, setMessageKey] = useState(first?.key ?? "");
  const selectedKey = useRef(messageKey);
  const message = source.messages.find((m) => m.key === messageKey);
  const [accountId, setAccountId] = useState("");
  const [calendarId, setCalendarId] = useState("primary");
  const account =
    accounts.data?.find((a) => a.accountId === accountId) ??
    accounts.data?.find((a) => a.email === source.defaultAccountEmail) ??
    accounts.data?.[0];
  const connected = account?.calendar.status === "connected";
  const [title, setTitle] = useState(first?.subject ?? "");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [startZone, setStartZone] = useState(source.defaultTimeZone);
  const [endZone, setEndZone] = useState(source.defaultTimeZone);
  const [allDay, setAllDay] = useState(false);
  const [reminder, setReminder] = useState("30");
  const [suggestions, setSuggestions] = useState<EmailEventSuggestion[]>([]);
  const [suggestionNotes, setSuggestionNotes] = useState<string[]>([]);
  const [preview, setPreview] = useState<(EmailEventPreview & { armedAt: number }) | null>(null);
  const [result, setResult] = useState<EmailEventResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  async function suggest() {
    const requested = messageKey;
    setBusy(true);
    setProblem("");
    try {
      const list = currentSuggestions(
        requested,
        selectedKey.current,
        await emailCalendarApi.suggest(requested),
      );
      if (!list) return; // The owner picked another email meanwhile; drop the old dates.
      setSuggestions(list);
      if (!list.length) setProblem("No clear event was found. You can enter the details yourself.");
    } catch (error) {
      if (requested === selectedKey.current) setProblem(errorWords(error));
    } finally {
      setBusy(false);
    }
  }
  function apply(s: EmailEventSuggestion) {
    setTitle(s.title);
    setLocation(s.location);
    setNotes(s.notes);
    setStart(s.startLocal ?? "");
    setEnd(s.endLocal ?? "");
    setStartZone(s.startTimeZone ?? "");
    setEndZone(s.endTimeZone ?? "");
    setAllDay(s.allDay);
    setSuggestionNotes([
      s.quote,
      ...s.warnings,
      ...(!s.startTimeZone || !s.endTimeZone
        ? ["Choose and check both time zones. The email did not specify them clearly."]
        : []),
    ]);
    setSuggestions([]);
    setProblem("");
  }
  async function review() {
    if (!account) return;
    if (!start || !end || !startZone || !endZone) {
      setProblem("Enter the start, end and both time zones before reviewing.");
      return;
    }
    const event: EmailEventInput = {
      messageKey,
      accountId: account.accountId,
      calendarId,
      title,
      location,
      notes,
      startLocal: start,
      endLocal: allDay ? nextDate(end) : end,
      startTimeZone: startZone,
      endTimeZone: endZone,
      allDay,
      reminderMinutes: reminder === "none" ? null : Number(reminder),
    };
    setBusy(true);
    setProblem("");
    try {
      const p = await emailCalendarApi.preview(event);
      setPreview({ ...p, armedAt: Date.now() });
    } catch (error) {
      setProblem(errorWords(error));
    } finally {
      setBusy(false);
    }
  }
  async function confirm() {
    if (!preview || Date.now() - preview.armedAt < CONFIRM_GUARD_MS) return;
    setBusy(true);
    setProblem("");
    try {
      const r = await emailCalendarApi.approve(preview.input, preview.fingerprint);
      setResult(r);
      void client.invalidateQueries({ queryKey: ["work"] });
      void client.invalidateQueries({ queryKey: ["agenda"] });
      if (!r.deduped)
        registerUndo({
          message: "Adding email event to your calendar…",
          durationMs: 10000,
          undo: () => inboxApi.cancelWork(r.work.id),
          doneMessage: () => "Cancelled. Nothing was added.",
          tooLateMessage:
            "The event is already being added. Open Google Calendar to edit or remove it.",
        });
    } catch (error) {
      setProblem(errorWords(error));
    } finally {
      setBusy(false);
    }
  }
  if (result) return <EventResult result={result} onClose={onClose} />;
  if (preview)
    return (
      <EventReview
        preview={preview}
        endInclusive={end}
        busy={busy}
        problem={problem}
        onConfirm={() => void confirm()}
        onBack={() => {
          setPreview(null);
          setProblem("");
        }}
        onClose={onClose}
      />
    );
  return (
    <div className="flex flex-col gap-3 p-5">
      <h2 className="text-2xl">Add to calendar</h2>
      <label className="flex flex-col gap-1 text-sm">
        Source email
        <select
          className={field}
          value={messageKey}
          onChange={(e) => {
            selectedKey.current = e.target.value;
            setMessageKey(e.target.value);
            setStart("");
            setEnd("");
            setLocation("");
            setNotes("");
            setAllDay(false);
            setProblem("");
            setStartZone(source.defaultTimeZone);
            setEndZone(source.defaultTimeZone);
            setSuggestions([]);
            setSuggestionNotes([]);
            setTitle(source.messages.find((m) => m.key === e.target.value)?.subject ?? "");
          }}
        >
          {source.messages.map((m) => (
            <option key={m.key} value={m.key}>
              {m.date.slice(0, 10)} · {m.from} · {m.subject}
            </option>
          ))}
        </select>
      </label>
      <details className="text-sm">
        <summary className="cursor-pointer text-muted">Read source text</summary>
        <p className="max-h-36 overflow-y-auto whitespace-pre-wrap py-2">{message?.excerpt}</p>
      </details>
      {message?.hasCalendarAttachment ? (
        <p className="text-xs text-muted">
          This email has a calendar attachment. Check it in Read email for the organizer’s original
          invite. Suggestions here use the email text.
        </p>
      ) : null}
      <div>
        <Button
          size="sm"
          disabled={busy || !source.aiAvailable}
          busy={busy}
          onClick={() => void suggest()}
        >
          Suggest details with Haiku
        </Button>
        <p className="mt-1 text-xs text-muted">
          Optional: sends this selected email’s text to Anthropic. Uses a small API call. Manual
          entry uses no AI credits.
        </p>
      </div>
      {suggestions.length ? (
        <div className="flex flex-col gap-2" aria-label="Suggested events">
          {suggestions.map((s, i) => (
            <Button
              key={i}
              className="h-auto justify-start py-2 text-left whitespace-normal"
              onClick={() => apply(s)}
            >
              {s.title} · {s.startLocal ?? "Check date"}
            </Button>
          ))}
        </div>
      ) : null}
      {suggestionNotes.length ? (
        <details className="rounded-lg border border-line p-2 text-xs" open>
          <summary>Source and things to check</summary>
          {suggestionNotes.map((n, i) => (
            <p className="mt-1 whitespace-pre-wrap" key={i}>
              {n}
            </p>
          ))}
        </details>
      ) : null}
      <label className="flex flex-col gap-1 text-sm">
        Google account
        <select
          className={field}
          value={account?.accountId ?? ""}
          onChange={(e) => {
            setAccountId(e.target.value);
            setCalendarId("primary");
          }}
        >
          {accounts.data?.map((a) => (
            <option key={a.accountId} value={a.accountId}>
              {a.email}
            </option>
          ))}
        </select>
      </label>
      {accounts.isError ? (
        <p role="alert">
          Couldn’t load calendars.{" "}
          <Button onClick={() => void accounts.refetch()}>Try again</Button>
        </p>
      ) : !connected ? (
        <div className="rounded-lg bg-sunk p-3 text-sm">
          <p>Connect Google Calendar for this account before adding events.</p>
          <Button
            className="mt-2"
            busy={busy}
            disabled={!account}
            onClick={() => {
              if (account) {
                setBusy(true);
                void calendarApi
                  .connect(account.accountId)
                  .then((r) => window.location.assign(r.url))
                  .catch((e) => {
                    setProblem(errorWords(e));
                    setBusy(false);
                  });
              }
            }}
          >
            Connect Google Calendar
          </Button>
          <p className="mt-1 text-xs text-muted">
            Google will ask for calendar access. After connecting, reopen this email to finish.
          </p>
        </div>
      ) : (
        <>
          <label className="flex flex-col gap-1 text-sm">
            Calendar
            <select
              className={field}
              value={calendarId}
              onChange={(e) => setCalendarId(e.target.value)}
            >
              {account.sources.map((s) => (
                <option key={s.calendarId} value={s.calendarId}>
                  {s.summary || s.calendarId}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Event title
            <input
              className={field}
              maxLength={400}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allDay}
              onChange={(e) => {
                setAllDay(e.target.checked);
                if (e.target.checked) setEndZone(startZone);
                setStart(
                  start
                    ? e.target.checked
                      ? start.slice(0, 10)
                      : `${start.slice(0, 10)}T09:00`
                    : "",
                );
                setEnd(
                  end ? (e.target.checked ? end.slice(0, 10) : `${end.slice(0, 10)}T10:00`) : "",
                );
              }}
            />
            All-day event
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="flex min-w-0 flex-col gap-1 text-sm">
              {allDay ? "First day" : "Start / departure"}
              <input
                className={field}
                type={allDay ? "date" : "datetime-local"}
                value={start}
                onInput={(e) => setStart(e.currentTarget.value)}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-sm">
              {allDay ? "Last day (included)" : "End / arrival"}
              <input
                className={field}
                type={allDay ? "date" : "datetime-local"}
                value={end}
                onInput={(e) => setEnd(e.currentTarget.value)}
                onChange={(e) => setEnd(e.target.value)}
              />
            </label>
            <Zone
              label={allDay ? "Time zone" : "Start time zone"}
              value={startZone}
              onChange={(zone) => {
                setStartZone(zone);
                if (allDay) setEndZone(zone);
              }}
            />
            {!allDay ? <Zone label="End time zone" value={endZone} onChange={setEndZone} /> : null}
          </div>
          <label className="flex flex-col gap-1 text-sm">
            Location / video-call link
            <input
              className={field}
              maxLength={1000}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Address, airport, or Zoom/Meet link"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Notes
            <textarea
              className={field}
              rows={2}
              maxLength={8000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Flight number, joining details or what to bring"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Reminder
            <select
              className={field}
              value={reminder}
              onChange={(e) => setReminder(e.target.value)}
            >
              <option value="none">None</option>
              {[0, 5, 10, 15, 30, 60, 120, 1440].map((n) => (
                <option key={n} value={n}>
                  {n === 0 ? "At start" : n === 1440 ? "1 day before" : `${n} minutes before`}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
      {problem ? (
        <p role="alert" className="text-sm">
          {problem}
        </p>
      ) : null}
      <div className="sticky -bottom-5 -mx-5 flex flex-wrap gap-2 border-t border-line bg-paper px-5 py-3">
        <Button
          variant="primary"
          busy={busy}
          disabled={!connected || !messageKey}
          onClick={() => void review()}
        >
          Review event
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
export function AddToCalendarButton({
  threadKey,
  command,
}: {
  threadKey: string;
  /** The keymap command that opens this dialog (⌘K and its key), while `enabled`. */
  command?: { id: CommandId; enabled: boolean };
}) {
  const [open, setOpen] = useState(false);
  // Opening only shows the form; adding the event still takes Review, then Confirm.
  useCommand(command?.id ?? "card.addEvent", () => setOpen(true), !!command?.enabled);
  const source = useQuery({
    queryKey: ["emailEventSource", threadKey],
    queryFn: () => emailCalendarApi.source(threadKey),
    enabled: open,
    retry: false,
  });
  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
        <CalendarPlus className="size-4" aria-hidden />
        Add to calendar
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        label="Add to calendar"
        className="mt-[4dvh] max-h-[92dvh]"
      >
        {source.isPending ? (
          <p className="p-5">Reading email details…</p>
        ) : source.isError ? (
          <div className="p-5">
            <p>Couldn’t read this email.</p>
            <Button onClick={() => void source.refetch()}>Try again</Button>
          </div>
        ) : !source.data.messages.length ? (
          <p className="p-5">This thread has no original email to use.</p>
        ) : (
          <EventForm source={source.data} onClose={() => setOpen(false)} />
        )}
      </Modal>
    </>
  );
}
